import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  CheckSquare,
  Award,
  Eye,
  Edit2,
  Trash2,
  PlusCircle,
  BookOpen,
  Folder,
  FolderOpen,
  ArrowLeft,
  Phone,
  Mail,
  User,
} from "lucide-react";
import { useAdminData } from "../context/AdminDataContext";
import Modal from "../components/Modal";
import StudentProfileDrawer from "../components/StudentProfileDrawer";
import { prelimsSubjectFolders } from "../data/initialQuestionBankData";

const PrelimsManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    prelimsTests,
    addPrelimsTest,
    updatePrelimsTest,
    deletePrelimsTest,
    togglePublishPrelimsTest,
    studentResults,
    prelimsQuestionBank,
  } = useAdminData();

  // Active section tab: 'tests' | 'results'
  const [activeTab, setActiveTab] = useState("tests");

  // Search & Filters
  const [testSearch, setTestSearch] = useState("");
  const [testFolderFilter, setTestFolderFilter] = useState("All");
  const [testDateFilter, setTestDateFilter] = useState("");
  const [resultSearch, setResultSearch] = useState("");

  // Modals
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [selectedStudentDetail, setSelectedStudentDetail] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [selectedTestForResults, setSelectedTestForResults] = useState(null);
  const [viewingProfileStudent, setViewingProfileStudent] = useState(null);
  const [resultStatusFilter, setResultStatusFilter] = useState("All");
  const [resultDateFilter, setResultDateFilter] = useState("");

  // Question Bank Picker Modal State
  const [isQbPickerOpen, setIsQbPickerOpen] = useState(false);
  const [qbActiveFolder, setQbActiveFolder] = useState(null);
  const [qbSearch, setQbSearch] = useState("");
  const [selectedQbIds, setSelectedQbIds] = useState([]);

  // Form state for creating / editing Prelims test
  const [testForm, setTestForm] = useState({
    title: "",
    folder: "Polity",
    date: new Date().toISOString().split("T")[0],
    category: "GS Paper I",
    description: "",
    duration: 15, // minutes
    totalMarks: 10,
    negativeMarking: 0.66,
    status: "Published",
    studyMaterialPdf: "",
    questions: [
      {
        id: "q1",
        questionNumber: 1,
        questionText: "",
        options: { A: "", B: "", C: "", D: "" },
        correctAnswer: "A",
        explanation: "",
      },
    ],
  });

  // URL trigger ?action=new
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openAddTestModal();
      searchParams.delete("action");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const openAddTestModal = () => {
    setEditingTest(null);
    setTestForm({
      title: "",
      folder: "Polity",
      date: new Date().toISOString().split("T")[0],
      category: "GS Paper I",
      description: "",
      duration: 15,
      totalMarks: 10,
      negativeMarking: 0.66,
      status: "Published",
      studyMaterialPdf: "",
      questions: [
        {
          id: `q-${Date.now()}-1`,
          questionNumber: 1,
          questionText: "With reference to the Indian Constitution, consider the following statements regarding the Attorney-General for India:",
          options: {
            A: "He is appointed by the Prime Minister of India.",
            B: "He has the right of audience in all courts in the territory of India.",
            C: "He holds office during the pleasure of the Chief Justice of India.",
            D: "He cannot take part in proceedings of either House of Parliament.",
          },
          correctAnswer: "B",
          explanation: "Under Article 76(3), in the performance of his duties, the Attorney-General has the right of audience in all courts in the territory of India.",
        },
      ],
    });
    setIsTestModalOpen(true);
  };

  const openEditTestModal = (test) => {
    setEditingTest(test);
    setTestForm({
      title: test.title || test.testName || "",
      folder: test.folder || test.subject || "Polity",
      date: test.date || new Date().toISOString().split("T")[0],
      category: test.category || "GS Paper I",
      description: test.description || "",
      duration: test.duration || 15,
      totalMarks: test.totalMarks || 10,
      negativeMarking: test.negativeMarking || 0.66,
      status: test.status || "Published",
      studyMaterialPdf: test.studyMaterialPdf || "",
      questions: test.questions && test.questions.length > 0 ? test.questions : [
        {
          id: "q1",
          questionNumber: 1,
          questionText: "Sample question text",
          options: { A: "Option A", B: "Option B", C: "Option C", D: "Option D" },
          correctAnswer: "A",
          explanation: "Sample explanation",
        },
      ],
    });
    setIsTestModalOpen(true);
  };

  // Add question to testForm
  const handleAddQuestion = () => {
    const qNum = testForm.questions.length + 1;
    setTestForm((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          id: `q-${Date.now()}-${qNum}`,
          questionNumber: qNum,
          questionText: "",
          options: { A: "", B: "", C: "", D: "" },
          correctAnswer: "A",
          explanation: "",
        },
      ],
    }));
  };

  // Import selected questions from Question Bank (prevents duplicates)
  const handleImportFromQb = () => {
    if (selectedQbIds.length === 0) return;
    const selectedQuestions = (prelimsQuestionBank || []).filter((q) =>
      selectedQbIds.includes(q.id)
    );

    const existingTexts = new Set(
      testForm.questions.map((q) => (q.questionText || "").trim().toLowerCase())
    );

    const newQuestions = [];
    let nextNum = testForm.questions.length + 1;

    selectedQuestions.forEach((sq) => {
      const normalized = (sq.questionText || "").trim().toLowerCase();
      if (!existingTexts.has(normalized)) {
        newQuestions.push({
          id: `q-qb-${sq.id}-${Date.now()}-${nextNum}`,
          questionNumber: nextNum++,
          questionText: sq.questionText,
          options: { ...sq.options },
          correctAnswer: sq.correctAnswer || "A",
          explanation: sq.explanation || "",
        });
        existingTexts.add(normalized);
      }
    });

    if (newQuestions.length === 0) {
      alert("All selected questions are already present in this test.");
      return;
    }

    setTestForm((prev) => ({
      ...prev,
      questions: [...prev.questions, ...newQuestions],
      totalMarks: Math.max(prev.totalMarks, (prev.questions.length + newQuestions.length) * 2),
    }));

    setSelectedQbIds([]);
    setIsQbPickerOpen(false);
  };

  // Remove question
  const handleRemoveQuestion = (idx) => {
    if (testForm.questions.length <= 1) {
      alert("A test must contain at least 1 question.");
      return;
    }
    setTestForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));
  };

  // Update specific question field
  const handleQuestionChange = (idx, field, value) => {
    setTestForm((prev) => {
      const updated = [...prev.questions];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, questions: updated };
    });
  };

  // Update question option
  const handleOptionChange = (qIdx, optKey, value) => {
    setTestForm((prev) => {
      const updated = [...prev.questions];
      updated[qIdx] = {
        ...updated[qIdx],
        options: {
          ...updated[qIdx].options,
          [optKey]: value,
        },
      };
      return { ...prev, questions: updated };
    });
  };

  const handleSaveTest = (e) => {
    e.preventDefault();
    if (!testForm.title.trim()) {
      alert("Please enter a test title.");
      return;
    }

    if (editingTest) {
      updatePrelimsTest(editingTest.id, {
        ...testForm,
        questionsCount: testForm.questions.length,
      });
    } else {
      addPrelimsTest({
        ...testForm,
        questionsCount: testForm.questions.length,
      });
    }
    setIsTestModalOpen(false);
  };

  // Filter tests by Search, Folder, and Upload/Test Date
  const filteredTests = (prelimsTests || []).filter((t) => {
    const titleMatch =
      (t?.title || t?.testName || "").toLowerCase().includes((testSearch || "").toLowerCase()) ||
      (t?.description || "").toLowerCase().includes((testSearch || "").toLowerCase());
    const folderMatch =
      testFolderFilter === "All" ||
      (t?.folder && t.folder.toLowerCase() === testFolderFilter.toLowerCase()) ||
      (t?.subject && t.subject.toLowerCase() === testFolderFilter.toLowerCase());
    const dateMatch =
      !testDateFilter ||
      (t?.date && t.date.startsWith(testDateFilter));
    return titleMatch && folderMatch && dateMatch;
  });

  // Filter prelims student results
  const prelimsResults = (studentResults || []).filter((r) => {
    if (!r || r.examType !== "Prelims") return false;

    if (selectedTestForResults) {
      const matchId =
        (r.testId && (String(r.testId) === String(selectedTestForResults.id) || String(r.testId) === String(selectedTestForResults._id))) ||
        (r.id && (String(r.id) === String(selectedTestForResults.id) || String(r.id) === String(selectedTestForResults._id)));
      const matchName =
        (r.initiativeName || r.testName || "").toLowerCase().trim() ===
        (selectedTestForResults.title || "").toLowerCase().trim();
      if (!matchId && !matchName) return false;
    }

    const matchesSearch =
      !resultSearch.trim() ||
      (r?.studentName || "").toLowerCase().includes(resultSearch.toLowerCase()) ||
      (r?.email || r?.studentEmail || "").toLowerCase().includes(resultSearch.toLowerCase()) ||
      (r?.phone || r?.mobileNumber || "").toLowerCase().includes(resultSearch.toLowerCase()) ||
      (r?.regId || "").toLowerCase().includes(resultSearch.toLowerCase()) ||
      (r?.initiativeName || r?.testName || "").toLowerCase().includes(resultSearch.toLowerCase());

    const matchesStatus =
      resultStatusFilter === "All" ||
      (r?.resultStatus || "Completed").toLowerCase() === resultStatusFilter.toLowerCase();

    const matchesDate =
      !resultDateFilter || (r?.submissionDate && r.submissionDate.startsWith(resultDateFilter));

    return matchesSearch && matchesStatus && matchesDate;
  });

  return (
    <div className="space-y-6 pb-12">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            Prelims Initiative Management
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Author free UPSC Prelims MCQ practice tests, calibrate marking schemes, and inspect individual student scorecards.
          </p>
        </div>

        <button
          onClick={openAddTestModal}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-xs shadow-lg shadow-emerald-700/20 transition-all transform active:scale-95"
        >
          <Plus size={16} /> Create MCQ Test
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-4 text-xs font-bold">
        <button
          onClick={() => setActiveTab("tests")}
          className={`pb-3 px-2 border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "tests"
              ? "border-emerald-700 text-emerald-800"
              : "border-transparent text-slate-400 hover:text-slate-700"
          }`}
        >
          <CheckSquare size={16} />
          <span>MCQ Tests ({prelimsTests.length})</span>
        </button>
        <button
          onClick={() => setActiveTab("results")}
          className={`pb-3 px-2 border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "results"
              ? "border-emerald-700 text-emerald-800"
              : "border-transparent text-slate-400 hover:text-slate-700"
          }`}
        >
          <Award size={16} />
          <span>Prelims Student Results ({prelimsResults.length})</span>
        </button>
      </div>

      {/* TAB 1: MCQ Tests Table */}
      {activeTab === "tests" && (
        <div className="space-y-4">
          {/* Search & Folder Filters Bar */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="relative flex-1 w-full max-w-md">
              <Search
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={testSearch}
                onChange={(e) => setTestSearch(e.target.value)}
                placeholder="Search Prelims tests by name or description..."
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-700/20 focus:border-emerald-700"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
              {/* Folder Selector Filter */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                  <Folder size={13} className="text-amber-600" /> Folder:
                </span>
                <select
                  value={testFolderFilter}
                  onChange={(e) => setTestFolderFilter(e.target.value)}
                  className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                >
                  <option value="All">All Folders</option>
                  {prelimsSubjectFolders.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>

              {/* Upload / Test Date Filter */}
              <input
                type="date"
                value={testDateFilter}
                onChange={(e) => setTestDateFilter(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                title="Filter by Upload / Test Date"
              />

              {(testSearch || testFolderFilter !== "All" || testDateFilter) && (
                <button
                  onClick={() => {
                    setTestSearch("");
                    setTestFolderFilter("All");
                    setTestDateFilter("");
                  }}
                  className="text-xs text-rose-600 font-bold hover:underline px-1.5"
                >
                  Clear
                </button>
              )}

              <span className="text-xs text-slate-500 font-medium pl-2 whitespace-nowrap">
                Showing: <strong>{filteredTests.length}</strong>
              </span>
            </div>
          </div>

          {/* Tests Table */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                    <th className="py-3.5 px-4">Test Name</th>
                    <th className="py-3.5 px-3">Folder</th>
                    <th className="py-3.5 px-3">Upload / Test Date</th>
                    <th className="py-3.5 px-3 text-center">Questions</th>
                    <th className="py-3.5 px-3 text-center">Total Marks</th>
                    <th className="py-3.5 px-3 text-center">Duration</th>
                    <th className="py-3.5 px-3 text-center">Participants</th>
                    <th className="py-3.5 px-3 text-center">Avg Score</th>
                    <th className="py-3.5 px-3 text-center">Status</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredTests.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-12 text-center text-slate-400">
                        No Prelims tests match the selected search, folder, or date filters.
                      </td>
                    </tr>
                  ) : (
                    filteredTests.map((test) => {
                      const isPublished = test.status === "Published";
                      const folderName = test.folder || test.subject || "Polity";
                      const testDateStr = test.date
                        ? new Date(test.date).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "Today";

                      return (
                        <tr
                          key={test.id}
                          className="hover:bg-slate-50/70 transition-colors group"
                        >
                          <td className="py-4 px-4 max-w-sm">
                            <div className="font-bold text-slate-900 group-hover:text-emerald-800 transition-colors line-clamp-1">
                              {test.title || test.testName}
                            </div>
                            <div className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">
                              {test.description || "Daily UPSC Prelims static & current affairs MCQ test."}
                            </div>
                          </td>
                          <td className="py-4 px-3 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-900 border border-amber-200">
                              <Folder size={11} className="text-amber-600" />
                              {folderName}
                            </span>
                          </td>
                          <td className="py-4 px-3 whitespace-nowrap font-mono text-[11px] text-slate-600">
                            {testDateStr}
                          </td>
                          <td className="py-4 px-3 text-center font-bold text-slate-700">
                            {test.questionsCount || (test.questions ? test.questions.length : 5)}
                          </td>
                          <td className="py-4 px-3 text-center font-bold text-slate-700">
                            {test.totalMarks || 10}
                          </td>
                          <td className="py-4 px-3 text-center font-mono text-slate-600">
                            {test.duration || 15}m
                          </td>
                          <td className="py-4 px-3 text-center font-semibold text-slate-800">
                            {test.participantsCount || 120}
                          </td>
                          <td className="py-4 px-3 text-center font-bold text-emerald-700">
                            {test.avgScore || 7.2} / {test.totalMarks || 10}
                          </td>
                          <td className="py-4 px-3 text-center whitespace-nowrap">
                            <button
                              onClick={() => togglePublishPrelimsTest(test.id)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${
                                isPublished
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-amber-50 text-amber-700 border border-amber-200"
                              }`}
                            >
                              {test.status}
                            </button>
                          </td>
                          <td className="py-4 px-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => openEditTestModal(test)}
                                className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"
                                title="Edit Test & Questions"
                              >
                                <Edit2 size={15} />
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(test.id)}
                                className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"
                                title="Delete Test"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: Prelims Results Table */}
      {activeTab === "results" && (
        <div className="space-y-4">
          {/* Breadcrumb banner when viewing a specific test */}
          {selectedTestForResults && (
            <div className="bg-gradient-to-r from-emerald-950 via-teal-950 to-[#0b1528] rounded-2xl p-5 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-md border border-emerald-800/30">
              <div>
                <button
                  onClick={() => setSelectedTestForResults(null)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300 hover:text-white mb-1.5 transition-colors"
                >
                  <ArrowLeft size={13} /> Back to All Prelims Tests
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-white">
                    {selectedTestForResults.title}
                  </h3>
                  <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-full bg-white/10 text-emerald-200 border border-white/15">
                    {selectedTestForResults.duration || 15}m • {selectedTestForResults.totalMarks || 10} marks
                  </span>
                </div>
                <span className="text-xs text-emerald-200/80 mt-1 block">
                  Showing all students who attended or submitted this mock test ({prelimsResults.length} records)
                </span>
              </div>
              <button
                onClick={() => setSelectedTestForResults(null)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold self-start sm:self-auto transition-all"
              >
                Show All Tests Results
              </button>
            </div>
          )}

          {/* Search and Filters Bar */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <Search
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={resultSearch}
                onChange={(e) => setResultSearch(e.target.value)}
                placeholder="Search by student name, email, mobile, or reg ID..."
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-700/20 focus:border-emerald-700"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              <select
                value={resultStatusFilter}
                onChange={(e) => setResultStatusFilter(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
              >
                <option value="All">All Statuses</option>
                <option value="Completed">Completed</option>
                <option value="Evaluated">Evaluated</option>
              </select>

              <input
                type="date"
                value={resultDateFilter}
                onChange={(e) => setResultDateFilter(e.target.value)}
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                title="Filter by submission date"
              />

              {(resultSearch || resultStatusFilter !== "All" || resultDateFilter) && (
                <button
                  onClick={() => {
                    setResultSearch("");
                    setResultStatusFilter("All");
                    setResultDateFilter("");
                  }}
                  className="px-3 py-2 text-rose-600 hover:bg-rose-50 rounded-xl font-bold transition-colors"
                >
                  Clear
                </button>
              )}

              <span className="text-xs text-slate-500 font-medium pl-2">
                Submissions: <strong>{prelimsResults.length}</strong>
              </span>
            </div>
          </div>

          {/* Clean Student Table */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                    <th className="py-3.5 px-4">Student Name</th>
                    <th className="py-3.5 px-3">Email Address</th>
                    <th className="py-3.5 px-3">Mobile Number</th>
                    <th className="py-3.5 px-3">Test Name</th>
                    <th className="py-3.5 px-2.5 text-center">Correct</th>
                    <th className="py-3.5 px-2.5 text-center">Incorrect</th>
                    <th className="py-3.5 px-2.5 text-center">Unanswered</th>
                    <th className="py-3.5 px-3 text-center">Marks</th>
                    <th className="py-3.5 px-2.5 text-center">%</th>
                    <th className="py-3.5 px-3">Date</th>
                    <th className="py-3.5 px-2.5 text-center">Status</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {prelimsResults.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="py-12 text-center text-slate-400">
                        No student test results match the selected filters.
                      </td>
                    </tr>
                  ) : (
                    prelimsResults.map((res) => (
                      <tr
                        key={res.id || res._id}
                        className="hover:bg-slate-50/70 transition-colors"
                      >
                        {/* Student Name */}
                        <td className="py-3.5 px-4 font-bold text-slate-900 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center flex-shrink-0">
                              {(res.studentName || "U")[0]}
                            </div>
                            <div>
                              <div className="hover:text-emerald-700 transition-colors">
                                {res.studentName}
                              </div>
                              <div className="text-[10px] text-slate-400 font-mono font-normal">
                                {res.regId || "SIA-2026-9001"}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Email Address */}
                        <td className="py-3.5 px-3 text-slate-600">
                          <a
                            href={`mailto:${res.email || res.studentEmail}`}
                            className="hover:text-emerald-700 hover:underline flex items-center gap-1 truncate max-w-[170px]"
                            title={res.email || res.studentEmail}
                          >
                            <Mail size={12} className="text-slate-400 flex-shrink-0" />
                            <span className="truncate">{res.email || res.studentEmail || "student@example.com"}</span>
                          </a>
                        </td>

                        {/* Mobile Number */}
                        <td className="py-3.5 px-3 font-mono text-slate-700 whitespace-nowrap">
                          <a
                            href={`tel:${(res.phone || res.mobileNumber || "+919840122334").replace(/\s+/g, "")}`}
                            className="hover:text-emerald-700 flex items-center gap-1"
                            title="Call student"
                          >
                            <Phone size={12} className="text-slate-400" />
                            <span>{res.phone || res.mobileNumber || "+91 98401 22334"}</span>
                          </a>
                        </td>

                        {/* Test Name */}
                        <td className="py-3.5 px-3 max-w-xs truncate text-slate-800 font-medium" title={res.initiativeName || res.testName}>
                          {res.initiativeName || res.testName}
                        </td>

                        {/* Correct Answers */}
                        <td className="py-3.5 px-2.5 text-center font-bold text-emerald-700">
                          <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                            {res.correctCount ?? (res.correctAnswers ?? 0)}
                          </span>
                        </td>

                        {/* Incorrect Answers */}
                        <td className="py-3.5 px-2.5 text-center font-bold text-rose-700">
                          <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-100">
                            {res.incorrectCount ?? (res.incorrectAnswers ?? 0)}
                          </span>
                        </td>

                        {/* Unanswered */}
                        <td className="py-3.5 px-2.5 text-center font-semibold text-slate-500">
                          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            {res.unansweredCount ?? (res.unanswered ?? 0)}
                          </span>
                        </td>

                        {/* Marks */}
                        <td className="py-3.5 px-3 text-center font-black text-slate-900 whitespace-nowrap">
                          {res.marksObtained != null ? res.marksObtained : 0} <span className="text-slate-400 text-[10px] font-normal">/ {res.totalMarks || 10}</span>
                        </td>

                        {/* Percentage */}
                        <td className="py-3.5 px-2.5 text-center font-bold text-emerald-700">
                          {res.percentage != null ? `${res.percentage}%` : "0%"}
                        </td>

                        {/* Date */}
                        <td className="py-3.5 px-3 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                          {res.submissionDate || "Today"}
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-2.5 text-center whitespace-nowrap">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            {res.resultStatus || "Completed"}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => setViewingProfileStudent(res)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-bold shadow-2xs transition-all"
                              title="View Student Profile & Performance History"
                            >
                              <User size={12} />
                              <span>View Profile</span>
                            </button>
                            <button
                              onClick={() => setSelectedStudentDetail(res)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-600 hover:text-white text-emerald-800 text-[11px] font-semibold border border-emerald-200 transition-all"
                              title="Inspect Question-Wise Scorecard"
                            >
                              <Eye size={12} />
                              <span>Scorecard</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* CREATE / EDIT TEST MODAL WITH QUESTION BUILDER */}
      {isTestModalOpen && (
        <Modal
          isOpen={isTestModalOpen}
          onClose={() => setIsTestModalOpen(false)}
          title={editingTest ? "Edit Prelims MCQ Test" : "Create Prelims MCQ Test"}
          subtitle="Configure duration, negative markings, questions, options, and explanations"
          maxWidth="max-w-4xl"
        >
          <form onSubmit={handleSaveTest} className="space-y-6">
            {/* General Info */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
              <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                Test Parameters
              </h4>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Test Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={testForm.title}
                    onChange={(e) =>
                      setTestForm({ ...testForm, title: e.target.value })
                    }
                    placeholder="e.g. Daily Prelims Quiz – 03 November 2026"
                    className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                      <Folder size={12} className="text-amber-600" /> Save in Folder *
                    </label>
                    <select
                      value={testForm.folder || "Polity"}
                      onChange={(e) =>
                        setTestForm({ ...testForm, folder: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                    >
                      {prelimsSubjectFolders.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Upload / Test Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={testForm.date}
                      onChange={(e) =>
                        setTestForm({ ...testForm, date: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Category
                    </label>
                    <select
                      value={testForm.category}
                      onChange={(e) =>
                        setTestForm({ ...testForm, category: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-700/20"
                    >
                      <option value="GS Paper I">GS Paper I (General Studies)</option>
                      <option value="CSAT">Paper II (CSAT)</option>
                      <option value="Current Affairs">Current Affairs Quiz</option>
                      <option value="Full Mock Test">Full Mock Test</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Duration (Minutes)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={testForm.duration}
                    onChange={(e) =>
                      setTestForm({ ...testForm, duration: Number(e.target.value) })
                    }
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-800"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Total Marks
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={testForm.totalMarks}
                    onChange={(e) =>
                      setTestForm({
                        ...testForm,
                        totalMarks: Number(e.target.value),
                      })
                    }
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-800"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Negative Marking
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={testForm.negativeMarking}
                    onChange={(e) =>
                      setTestForm({
                        ...testForm,
                        negativeMarking: Number(e.target.value),
                      })
                    }
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-800"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Status
                  </label>
                  <select
                    value={testForm.status}
                    onChange={(e) =>
                      setTestForm({ ...testForm, status: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-800"
                  >
                    <option value="Published">Published</option>
                    <option value="Draft">Draft</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Questions Builder Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-bold text-slate-900">
                    Questions Builder ({testForm.questions.length})
                  </h4>
                  <p className="text-xs text-slate-500">
                    Add questions with 4 options, designate the correct choice, and supply standard UPSC explanations.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedQbIds([]);
                      setIsQbPickerOpen(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 text-xs font-bold transition-colors"
                  >
                    <BookOpen size={14} /> Select from Question Bank ({prelimsQuestionBank?.length || 0})
                  </button>
                  <button
                    type="button"
                    onClick={handleAddQuestion}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold shadow transition-colors"
                  >
                    <PlusCircle size={15} /> Add Another Question
                  </button>
                </div>
              </div>

              {/* Questions Accordion / List */}
              <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
                {testForm.questions.map((q, qIdx) => (
                  <div
                    key={q.id || qIdx}
                    className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm space-y-3"
                  >
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                      <span className="font-bold text-xs text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-md">
                        Question #{qIdx + 1}
                      </span>
                      {testForm.questions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveQuestion(qIdx)}
                          className="text-rose-600 hover:text-rose-800 text-xs font-semibold flex items-center gap-1"
                        >
                          <Trash2 size={13} /> Remove
                        </button>
                      )}
                    </div>

                    {/* Question text */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Question Text *
                      </label>
                      <textarea
                        rows={2}
                        required
                        value={q.questionText}
                        onChange={(e) =>
                          handleQuestionChange(qIdx, "questionText", e.target.value)
                        }
                        placeholder="Type question prompt..."
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800"
                      />
                    </div>

                    {/* Options A, B, C, D */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {["A", "B", "C", "D"].map((opt) => (
                        <div key={opt} className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-lg bg-slate-100 text-slate-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                            {opt}
                          </span>
                          <input
                            type="text"
                            required
                            value={q.options[opt] || ""}
                            onChange={(e) =>
                              handleOptionChange(qIdx, opt, e.target.value)
                            }
                            placeholder={`Option ${opt} text`}
                            className="flex-1 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Correct answer & Explanation */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                      <div>
                        <label className="block text-xs font-bold text-emerald-800 mb-1">
                          Correct Answer *
                        </label>
                        <select
                          value={q.correctAnswer}
                          onChange={(e) =>
                            handleQuestionChange(qIdx, "correctAnswer", e.target.value)
                          }
                          className="w-full px-3 py-1.5 bg-emerald-50 border border-emerald-300 rounded-lg text-xs font-bold text-emerald-900"
                        >
                          <option value="A">Option A</option>
                          <option value="B">Option B</option>
                          <option value="C">Option C</option>
                          <option value="D">Option D</option>
                        </select>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Explanation & Reference Notes
                        </label>
                        <input
                          type="text"
                          value={q.explanation || ""}
                          onChange={(e) =>
                            handleQuestionChange(qIdx, "explanation", e.target.value)
                          }
                          placeholder="Why this answer is correct..."
                          className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Buttons */}
            <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsTestModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 text-xs font-bold text-white bg-emerald-700 hover:bg-emerald-600 rounded-xl shadow-md transition-all"
              >
                {editingTest ? "Update Test" : "Publish MCQ Test"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* INSPECT STUDENT PRELIMS RESULT MODAL */}
      {selectedStudentDetail && (
        <Modal
          isOpen={!!selectedStudentDetail}
          onClose={() => setSelectedStudentDetail(null)}
          title={`Prelims Result: ${selectedStudentDetail.studentName}`}
          subtitle={`Registration ID: ${selectedStudentDetail.regId} • Submitted on ${selectedStudentDetail.submissionDate}`}
          maxWidth="max-w-3xl"
        >
          <div className="space-y-5">
            {/* Score banner */}
            <div className="p-5 rounded-2xl bg-gradient-to-r from-emerald-950 via-slate-900 to-slate-900 text-white flex items-center justify-between shadow-lg">
              <div>
                <p className="text-xs text-emerald-400 font-semibold">
                  {selectedStudentDetail.initiativeName}
                </p>
                <h4 className="text-xl font-bold mt-1">
                  {selectedStudentDetail.studentName}
                </h4>
                <p className="text-xs text-slate-300">
                  {selectedStudentDetail.studentEmail || "aspirant@upsc.gov.in"}
                </p>
              </div>
              <div className="text-right">
                <span className="text-3xl font-black text-amber-400">
                  {selectedStudentDetail.marksObtained}
                </span>
                <span className="text-sm text-slate-300 font-medium">
                  {" "}
                  / {selectedStudentDetail.totalMarks}
                </span>
                <p className="text-xs text-emerald-400 font-bold mt-0.5">
                  Accuracy: {selectedStudentDetail.percentage}%
                </p>
                <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-400/20 text-amber-300 border border-amber-400/30">
                  All-India Rank: #{selectedStudentDetail.rank || 1}
                </span>
              </div>
            </div>

            {/* Answer breakdown stats */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                <p className="text-xs text-emerald-800 font-bold">Correct</p>
                <p className="text-xl font-black text-emerald-900">
                  {selectedStudentDetail.correctCount ?? 4}
                </p>
              </div>
              <div className="p-3 bg-rose-50 rounded-xl border border-rose-200">
                <p className="text-xs text-rose-800 font-bold">Incorrect</p>
                <p className="text-xl font-black text-rose-900">
                  {selectedStudentDetail.incorrectCount ?? 1}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                <p className="text-xs text-slate-700 font-bold">Unanswered</p>
                <p className="text-xl font-black text-slate-800">
                  {selectedStudentDetail.unansweredCount ?? 0}
                </p>
              </div>
            </div>

            {/* Question by question inspection */}
            <div>
              <h5 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
                Question-Wise Answer Audit
              </h5>
              <div className="space-y-3">
                {[
                  {
                    qNum: 1,
                    text: "With reference to the Parliamentary Committees in India, consider the following statements regarding the Business Advisory Committee...",
                    studentChoice: "A",
                    correctChoice: "A",
                    isCorrect: true,
                    explanation: "Under Rules of Procedure, the Business Advisory Committee is chaired by the Lok Sabha Speaker.",
                  },
                  {
                    qNum: 2,
                    text: "Consider the following statements regarding the Mineral Security Partnership (MSP)...",
                    studentChoice: "C",
                    correctChoice: "B",
                    isCorrect: false,
                    explanation: "India joined the US-led MSP in June 2023. MSP is not an agency under the United Nations.",
                  },
                  {
                    qNum: 3,
                    text: "With reference to Quantum Computing, which of the following best defines 'Quantum Supremacy'?",
                    studentChoice: "B",
                    correctChoice: "B",
                    isCorrect: true,
                    explanation: "Quantum supremacy refers to a programmable quantum device outperforming classical supercomputers on a designated benchmark.",
                  },
                ].map((item) => (
                  <div
                    key={item.qNum}
                    className={`p-3.5 rounded-xl border text-xs space-y-2 ${
                      item.isCorrect
                        ? "bg-emerald-50/40 border-emerald-200"
                        : "bg-rose-50/40 border-rose-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-800">
                        Q{item.qNum}. {item.text}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          item.isCorrect
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-rose-100 text-rose-800"
                        }`}
                      >
                        {item.isCorrect ? "Correct (+2.0)" : "Incorrect (-0.66)"}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-[11px]">
                      <span>
                        Student Answer:{" "}
                        <strong
                          className={
                            item.isCorrect ? "text-emerald-700" : "text-rose-700"
                          }
                        >
                          Option {item.studentChoice}
                        </strong>
                      </span>
                      <span>
                        Correct Answer:{" "}
                        <strong className="text-emerald-700">
                          Option {item.correctChoice}
                        </strong>
                      </span>
                    </div>

                    <p className="text-[11px] text-slate-600 bg-white/70 p-2 rounded-lg border border-slate-200">
                      <strong>Explanation:</strong> {item.explanation}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <Modal
          isOpen={!!deleteConfirmId}
          onClose={() => setDeleteConfirmId(null)}
          title="Delete Prelims MCQ Test"
          maxWidth="max-w-md"
          footer={
            <div className="w-full flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deletePrelimsTest(deleteConfirmId);
                  setDeleteConfirmId(null);
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-rose-700 hover:bg-rose-800 rounded-xl shadow"
              >
                Delete Test
              </button>
            </div>
          }
        >
          <p className="text-xs text-slate-600 leading-relaxed">
            Are you sure you want to permanently delete this Prelims MCQ test? This will remove all associated question data.
          </p>
        </Modal>
      )}

      {/* Question Bank Selection Modal (Folder-Based) */}
      {isQbPickerOpen && (
        <Modal
          isOpen={isQbPickerOpen}
          onClose={() => {
            setIsQbPickerOpen(false);
            setQbActiveFolder(null);
          }}
          title="Browse Prelims Question Bank Folders"
          subtitle="Open subject folders to select questions. Duplicate questions are automatically prevented."
          maxWidth="max-w-2xl"
          footer={
            <div className="w-full flex items-center justify-between">
              <span className="text-xs text-slate-700">
                <strong>{selectedQbIds.length}</strong> question(s) selected{" "}
                <span className="text-red-900 font-bold">
                  ({selectedQbIds.length * 2} Marks)
                </span>
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsQbPickerOpen(false);
                    setQbActiveFolder(null);
                  }}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleImportFromQb}
                  disabled={selectedQbIds.length === 0}
                  className="px-4 py-1.5 bg-red-900 hover:bg-red-800 disabled:opacity-50 text-white rounded-lg text-xs font-bold shadow-xs"
                >
                  Add {selectedQbIds.length > 0 ? `(${selectedQbIds.length}) ` : ""}to Test
                </button>
              </div>
            </div>
          }
        >
          <div className="space-y-3 text-xs">
            {/* If NO folder selected: Show Folder Grid */}
            {!qbActiveFolder ? (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 font-medium">
                  Select a subject folder to view and select questions:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-h-72 overflow-y-auto pr-1">
                  {prelimsSubjectFolders.map((subj) => {
                    const count = (prelimsQuestionBank || []).filter(
                      (q) => (q?.subject || "").trim().toLowerCase() === subj.toLowerCase()
                    ).length;

                    return (
                      <div
                        key={subj}
                        onClick={() => {
                          setQbActiveFolder(subj);
                          setQbSearch("");
                        }}
                        className="p-3 bg-slate-50 hover:bg-red-50/50 border border-slate-200 hover:border-red-900/40 rounded-xl transition-all cursor-pointer group flex flex-col justify-between"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Folder className="w-5 h-5 text-red-900 group-hover:scale-110 transition-transform" />
                          <span className="text-[10px] font-bold text-slate-600 bg-white border border-slate-200 px-1.5 py-0.5 rounded-full">
                            {count}
                          </span>
                        </div>
                        <p className="font-bold text-slate-800 group-hover:text-red-950 text-xs truncate">
                          {subj}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* If Folder IS selected: Show Questions in that folder */
              <div className="space-y-3">
                {/* Breadcrumb & Search */}
                <div className="flex items-center justify-between gap-2 pb-2 border-b border-slate-100">
                  <button
                    type="button"
                    onClick={() => setQbActiveFolder(null)}
                    className="flex items-center gap-1 text-xs font-bold text-red-900 hover:underline"
                  >
                    <ArrowLeft size={13} /> Back to Folders
                  </button>
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-slate-500">Folder:</span>
                    <span className="font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded flex items-center gap-1">
                      <FolderOpen size={12} className="text-red-900" />
                      {qbActiveFolder}
                    </span>
                  </div>
                </div>

                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={qbSearch}
                    onChange={(e) => setQbSearch(e.target.value)}
                    placeholder={`Search within ${qbActiveFolder}...`}
                    className="w-full pl-8 pr-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-900"
                  />
                </div>

                {/* Questions List */}
                <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 pr-1">
                  {(prelimsQuestionBank || [])
                    .filter((q) => {
                      const matchesFolder =
                        (q?.subject || "").trim().toLowerCase() === qbActiveFolder.toLowerCase();
                      const matchesSearch =
                        !qbSearch.trim() ||
                        (q?.questionText || "").toLowerCase().includes(qbSearch.toLowerCase()) ||
                        (q?.topic || "").toLowerCase().includes(qbSearch.toLowerCase());
                      return matchesFolder && matchesSearch;
                    })
                    .map((q) => {
                      const isAlreadyInTest = testForm.questions.some(
                        (tq) =>
                          (tq.questionText || "").trim().toLowerCase() ===
                          (q.questionText || "").trim().toLowerCase()
                      );
                      const isChecked = selectedQbIds.includes(q.id);

                      return (
                        <div
                          key={q.id}
                          className={`p-2.5 flex items-start gap-3 rounded-lg transition-colors ${
                            isAlreadyInTest
                              ? "opacity-60 bg-slate-50/50"
                              : "hover:bg-slate-50 cursor-pointer"
                          }`}
                          onClick={() => {
                            if (isAlreadyInTest) return;
                            if (isChecked) {
                              setSelectedQbIds((prev) => prev.filter((id) => id !== q.id));
                            } else {
                              setSelectedQbIds((prev) => [...prev, q.id]);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isAlreadyInTest}
                            onChange={() => {}}
                            className="mt-0.5 rounded text-red-900 focus:ring-red-900 cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-900 line-clamp-2 leading-relaxed">
                              {q.questionText}
                            </p>
                            <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500">
                              <span className="font-extrabold text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded">
                                &lt;CO&gt; {q.correctAnswer}
                              </span>
                              <span>•</span>
                              <span className="font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                                {q.topic || "General"}
                              </span>
                              <span>•</span>
                              <span className="text-slate-500">Diff: {q.difficulty || "Medium"}</span>
                            </div>
                          </div>
                          {isAlreadyInTest && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500 flex-shrink-0">
                              Already in test
                            </span>
                          )}
                        </div>
                      );
                    })}

                  {(prelimsQuestionBank || []).filter(
                    (q) =>
                      (q?.subject || "").trim().toLowerCase() === qbActiveFolder.toLowerCase()
                  ).length === 0 && (
                    <div className="py-8 text-center text-xs text-slate-400">
                      No questions found in this subject folder yet.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* STUDENT PROFILE DRAWER */}
      <StudentProfileDrawer
        isOpen={!!viewingProfileStudent}
        onClose={() => setViewingProfileStudent(null)}
        student={viewingProfileStudent}
        mode="prelims"
        onInspectPrelimsResult={(res) => {
          setViewingProfileStudent(null);
          setSelectedStudentDetail(res);
        }}
      />
    </div>
  );
};

export default PrelimsManagement;
